import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import {
  User,
  Trophy,
  Coins,
  Target,
  Bell,
  Gift,
  TrendingUp,
  BookOpen,
  BookMarked,
  FlaskConical,
  CalendarDays,
  ClipboardList,
  Pencil,
  Zap,
  Users,
  GraduationCap,
  ChevronDown,
  BarChart2,
} from "lucide-react";
import UserSettingsModal from "../components/UserSettingsModal";

import "../styles/homePage.css";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";
import { getSession } from "../session";
import levelsData from "../data/levels.json";

export default function HomePage() {
  // username from localStorage, rest default
  const [user, setUser] = useState({
    name:
      localStorage.getItem("displayName") ||
      localStorage.getItem("username") ||
      "Guest",
    coins: 0,
    level: 0,
    xp: 0,
    progressPct: 0,
    xpToNext: 1000,
    streak: 0,
    rank: "Scholar",
    dailySpins: 0,
    completedToday: false,
  });
  const [bookCount, setBookCount] = useState(null);
  const [bonusAvailable, setBonusAvailable] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(
    () => sessionStorage.getItem("hp_conn_status") || null
  ); // null | "none" | "pending" | "accepted"
  const [showSettings, setShowSettings] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [dailyDone, setDailyDone] = useState(false);
  const [dailyProgress, setDailyProgress] = useState(null); // { completed, total } | null
  const session = getSession();

  useEffect(() => {
    document.title = "Home — Owllet Reading Quest";
  }, []);

  useEffect(() => {
    document.body.classList.add("has-page-header");
    return () => document.body.classList.remove("has-page-header");
  }, []);

  // silently cache user owls for the loading overlay
  useEffect(() => {
    const uid = localStorage.getItem("userId");
    if (!uid) return;
    apiFetch(`/api/collection?userId=${uid}`)
      .then(r => r.json())
      .then(owls => {
        if (Array.isArray(owls) && owls.length)
          localStorage.setItem("owlCache", JSON.stringify(owls.map(o => ({ name: o.name, image_url: o.image_url }))));
      })
      .catch(() => {});
  }, []);

  // fetch coins and xp from backend
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (!storedUserId) return;

    // Fetch coins
    apiFetch(`/api/resources/${storedUserId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch coins");
        return res.json();
      })
      .then((data) => {
        if (typeof data.coins === "number") {
          setUser((prev) => ({ ...prev, coins: data.coins }));
        }
      })
      .catch((err) => console.error("Coins fetch error:", err));

    // Fetch Level + XP
    apiFetch(`/api/resources/xp/${storedUserId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch level");
        return res.json();
      })
      .then((data) => {
        setUser((prev) => ({
          ...prev,
          level: typeof data.level === "number" ? data.level : prev.level,
          xp: typeof data.xp === "number" ? data.xp : prev.xp,
          progressPct:
            typeof data.progressPct === "number"
              ? data.progressPct
              : prev.progressPct,
          xpToNext:
            typeof data.xpToNext === "number" ? data.xpToNext : prev.xpToNext,
        }));
      })
      .catch((err) => console.error("Level fetch error:", err));

    // Fetch enabled book count from catalogue
    apiFetch(`/api/books/catalogue`)
      .then((res) => res.json())
      .then((data) => {
        const count = Object.keys(data.bookInfo || {}).length;
        setBookCount(count);
      })
      .catch(() => {});

    // Fetch streak from calendar API (also triggers check-in)
    apiFetch(`/api/calendar/checkin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: parseInt(storedUserId, 10) }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (typeof data.streak === "number") {
          setUser((prev) => ({ ...prev, streak: data.streak }));
        }
        if (data.bonusAvailable) {
          setBonusAvailable(true);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch connection status for non-teacher accounts (run once on mount)
  useEffect(() => {
    if (!session || session.isTeacher) return;
    apiFetch("/api/student/connection")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          const status = data.status || "none";
          setConnectionStatus(status);
          sessionStorage.setItem("hp_conn_status", status);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // Fetch daily tasks completion status for the home card
  useEffect(() => {
    if (!session || session.isTeacher) return;
    apiFetch("/api/daily-tasks")
      .then((r) => (r.ok ? r.json() : null))
      .then((tasks) => {
        if (!Array.isArray(tasks) || tasks.length === 0) return;
        const total     = tasks.length;
        const completed = tasks.filter((t) => t.completed_at).length;
        setDailyProgress({ completed, total });
        setDailyDone(completed === total);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const notifications = ["NONE"];

  function getDailyDescription(done, progress) {
    if (done) return '🦉 "Magnificent! The wise owl has spoken — all done today!"';
    if (!progress || progress.completed === 0)
      return '🦉 "Hoot hoot! Your tasks are ready — let\'s get reading!"';
    const remaining = progress.total - progress.completed;
    if (remaining === 1)
      return '🦉 "You\'re almost there, wise one. One last challenge awaits!"';
    return `🦉 "Nice work! Keep flying — ${remaining} tasks left to rule the roost."`;
  }

  const navItems = [
    // Daily Tasks card — first, shown to logged-in students
    ...(!session?.isTeacher && session
      ? [
          {
            title: "Daily Tasks",
            description: getDailyDescription(dailyDone, dailyProgress),
            icon: dailyDone
              ? <span className="dt-owl-celebration">🦉</span>
              : <CalendarDays size={24} />,
            status: dailyProgress
              ? `${dailyProgress.completed}/${dailyProgress.total} complete`
              : "",
            color: "nav-premium",
            link: "/daily-tasks",
            featured: true,
            allDone: dailyDone,
          },
        ]
      : []),
    {
      title: "Comprehension",
      description:
        "Climb the ranks — spot words, crush MCQs, and battle in ranked mode",
      icon: <BookOpen size={24} />,
      status: "",
      color: "nav-blue",
      link: "/games/comprehension",
    },
    {
      title: "Books",
      description:
        "Dive into stories, read chapters, and prove you understood every word",
      icon: <BookMarked size={24} />,
      status: "",
      color: "nav-purple",
      link: "/games/books",
    },
    {
      title: "Vocabulary Practice",
      description:
        "Train your word power — matching, synonyms, antonyms and more",
      icon: <TrendingUp size={24} />,
      status: "",
      color: "nav-teal",
      link: "/games/vocabulary",
    },
    {
      title: "Vocabulary Tests",
      description:
        "Put your vocab to the test — definitions, synonyms, antonyms and mixed challenges",
      icon: <FlaskConical size={24} />,
      status: "",
      color: "nav-pink",
      link: "/test-modes",
    },
    {
      title: "My Reports and Results",
      description:
        "Track your progress, see how far you've come and celebrate your wins",
      icon: <Trophy size={24} />,
      status: "",
      color: "nav-orange",
      link: "/statisticsPage",
    },
    {
      title: "My Owl Collections",
      description:
        "Meet your owls — see every one you've hatched and collected",
      icon: <Target size={24} />,
      status: "",
      color: "nav-red",
      link: "/owlNest",
    },
    // Teacher card
    ...(session?.isTeacher
      ? [
          {
            title: "My Class",
            description:
              "Your classroom hub — manage students, set assignments and approve requests",
            icon: <GraduationCap size={24} />,
            status: "Teacher dashboard",
            color: "nav-indigo",
            link: "/my-class",
          },
        ]
      : []),
    // Student card — join class or view homework
    ...(!session?.isTeacher && session
      ? [
          connectionStatus === "accepted"
            ? {
                title: "Homework",
                description:
                  "Check what your teacher has set — complete tasks and earn rewards",
                icon: <Pencil size={24} />,
                status: "",
                color: "nav-premium",
                link: "/homework",
              }
            : {
                title: "Join a Class",
                description:
                  connectionStatus === "pending"
                    ? "Connection request pending — waiting for teacher approval"
                    : "Got a code? Join your class and start receiving assignments from your teacher",
                icon: <Users size={24} />,
                status:
                  connectionStatus === "pending"
                    ? "Request pending"
                    : "Connect with a teacher",
                color: "nav-premium",
                link: "/join-class",
              },
        ]
      : []),
  ];

  const tierKey = localStorage.getItem("difficulty_tier");
  const tierName =
    tierKey && levelsData.tiers[tierKey]
      ? levelsData.tiers[tierKey].name
      : null;

  return (
    <div className="homepage">
      <header className="header">
        <div className="header-left">
          <div className="logo">
            <img
              src={`${process.env.PUBLIC_URL}/images/owlletLogo.png`}
              alt="Owllet"
              style={{ width: "96px", height: "96px", objectFit: "contain" }}
            />
          </div>
          <div>
            <h1 className="title">Owllet Reading Quest</h1>
            <p className="subtitle">Master comprehension through adventure</p>
          </div>
          <span className="header-left-mobile-title">
            <BarChart2 size={16} aria-hidden="true" />
            My Stats
          </span>
        </div>
        <button
          className="stats-toggle-btn"
          onClick={() => setStatsOpen((o) => !o)}
          aria-expanded={statsOpen}
          aria-controls="header-collapsible"
          aria-label={statsOpen ? "Hide stats" : "Show stats"}
        >
          <ChevronDown
            size={18}
            className={`stats-toggle-icon${statsOpen ? " open" : ""}`}
            aria-hidden="true"
          />
        </button>
        <div
          id="header-collapsible"
          className={`header-collapsible${statsOpen ? "" : " header-collapsible--hidden"}`}
        >
        <dl id="stats-bar" className="stats-bar">
          <Link to="/owlCollectingGacha" className="coins-card-link">
            <div className="stat-card yellow stat-card--clickable">
              {user.coins >= 200 && (
                <span
                  className="streak-badge"
                  aria-label="Qualified for Owl spin"
                >
                  !
                </span>
              )}
              <img
                src={`${process.env.PUBLIC_URL}/images/chest_img.png`}
                className="stat-card-img"
                alt="Coins"
              />
              <div>
                <dd className="stat-value">{user.coins}</dd>
                <dt
                  className={`stat-label${user.coins >= 200 ? " stat-label--qualified" : ""}`}
                >
                  {user.coins >= 200 ? (
                    <>
                      <span className="label-desktop">Hatch an Owl!</span>
                      <span className="label-mobile">Hatch!</span>
                    </>
                  ) : (
                    "Coins"
                  )}
                </dt>
              </div>
            </div>
          </Link>
          <Link to="/calendar" className="streak-card-link">
            <div className="stat-card green stat-card--clickable">
              {bonusAvailable && (
                <span
                  className="streak-badge"
                  aria-label="Daily bonus available"
                >
                  !
                </span>
              )}
              <img
                src={`${process.env.PUBLIC_URL}/images/calendar_img.png`}
                className="stat-card-img"
                alt="Calendar"
              />
              <div>
                <dd className="stat-value">{user.streak}</dd>
                <dt className="stat-label">Day Streak</dt>
              </div>
            </div>
          </Link>
          <div className="stat-card purple">
            <img
              src={`${process.env.PUBLIC_URL}/images/trophy_img.png`}
              className="stat-card-img"
              alt="Trophy"
            />
            <div>
              <dd className="stat-value"> </dd>
              <dt className="stat-label"> </dt>
            </div>
          </div>
          <div className="stat-card blue stat-card--xp">
            <Zap size={24} className="stat-xp-icon" aria-hidden="true" />
            <div className="stat-xp-wrap">
              <div className="stat-xp-header">
                <span className="stat-xp-level">Level {user.level}</span>
                <span className="stat-xp-sub">
                  {user.xp} / {user.xp + user.xpToNext} XP
                </span>
              </div>
              <div className="stat-xp-track">
                <div
                  className="stat-xp-fill"
                  style={{ width: `${Math.min(100, user.progressPct)}%` }}
                />
              </div>
              {tierName && <span className="stat-xp-tier">{tierName}</span>}
            </div>
          </div>
          <div
            className="stat-card stat-card--profile stat-card--clickable"
            onClick={() => setShowSettings(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setShowSettings(true)}
            aria-label="Open preferences"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/owlTestImgNoBG.png`}
              className="stat-card-img"
              alt="Profile"
            />
            <div>
              <dd className="stat-value stat-value--profile">{user.name}</dd>
              <dt className="stat-label">Profile</dt>
            </div>
          </div>
        </dl>
        <UserSettingsModal open={showSettings} onOpenChange={setShowSettings} />
        </div>
      </header>

      <main className="main-content">
        <div className="nav-grid">
          {navItems.map((item, index) => (
            <div
              key={index}
              className={`nav-card ${item.urgent ? "urgent" : ""} ${item.comingSoon ? "nav-card-coming-soon" : ""} ${item.featured ? "nav-card--featured" : ""} ${item.allDone ? "nav-card--all-done" : ""}`}
            >
              <div className="nav-card-header">
                <div className={`nav-icon ${item.color}`}>{item.icon}</div>
                <h3>{item.title}</h3>
                {item.featured && !item.allDone && <span className="today-badge">Today</span>}
                {item.allDone && <span className="done-badge">🎉 Done!</span>}
                {item.urgent && <span className="urgent-badge">New</span>}
                {item.comingSoon && (
                  <span className="coming-soon-badge">Coming Soon</span>
                )}
              </div>
              <p>{item.description}</p>
              <div className="nav-footer">
                <span>{item.status}</span>
                {!item.comingSoon && (
                  <Link to={item.link}>
                    <button className="enter-btn">Enter →</button>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
